import React from 'react';
import { batch } from 'react-redux';
import { recommendationsIdSelector, recommendationsTypeSelector } from 'modules/recommendations/selectors';
import { addIndication, INDICATION_TYPES } from 'modules/taboola-common-frontend-modules/Indications';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { selectedAccountSelector } from 'selectors';
import { campaignsApi } from 'services/api';
import { errorMessagesUtils } from '../../../services/utils';
import { isNetworkAccountType } from '../../account-management';
import { isProgrammaticAccount } from '../../account-management/accountType';
import { duplicateCampaignError, duplicateCampaignSuccess, requestDuplicateCampaign } from '../actions';
import { CAMPAIGN_REPORT_ID } from '../modules/campaigns-reports/config/reports/campaign/campaignReportId';
import { CREATIVE_REPORT_ID } from '../modules/campaigns-reports/config/reports/creative/creativeReportId';
import { fromGWtransformers, toGWtransformers } from './flowsUtils';
import selectCampaignAndReport from './selectCampaignAndReport';

const successIndication = {
    message: <FormattedMessage id="campaign.duplicate.success" defaultMessage="Campaign successfully duplicated" />,
    type: INDICATION_TYPES.SUCCESS,
    highlight: <FormattedMessage id="success.highlight" defaultMessage="Success!" />,
};

const generateErrorIndication = (errorObject = {}) => ({
    message: errorMessagesUtils.getErrorMessage(errorObject),
    type: INDICATION_TYPES.ERROR,
    highlight: (
        <FormattedMessage id="campaign.duplicate.error.highlight" defaultMessage="Unable to duplicate the campaign." />
    ),
});

const generatePartialSuccessIndication = (successCount, totalCount) => ({
    message: (
        <FormattedMessage
            id="campaign.duplicate.partial.success"
            defaultMessage="Successfully duplicated {successCount} of {totalCount} campaigns"
            values={{ successCount, totalCount }}
        />
    ),
    type: INDICATION_TYPES.WARNING,
    highlight: <FormattedMessage id="partial.success.highlight" defaultMessage="Partial Success" />,
});

const duplicateCampaign =
    ({ accountId, campaignId, changes, passedDestinationAccountName, entityType, navigate }) =>
    async (dispatch, getState) => {
        const campaignChanges = toGWtransformers[entityType](changes || {});
        const { duplicateCampaignSettings: { numberOfCopies = 1 } = {} } = changes;
        const state = getState();
        const recommendationType = recommendationsTypeSelector(state);
        const recommendationId = recommendationsIdSelector(state);
        const account = selectedAccountSelector(state);
        const { type } = account;
        const destinationAccountName = isNetworkAccountType(type) ? passedDestinationAccountName : '';

        dispatch(requestDuplicateCampaign());

        try {
            const duplicatePromises = Array.from({ length: numberOfCopies }, (_, index) =>
                campaignsApi.duplicateCampaign({
                    accountId,
                    campaignId,
                    changes: {
                        ...campaignChanges,
                        name: index === 0 ? campaignChanges.name : `${campaignChanges.name} (${index + 1})`,
                    },
                    destinationAccount: destinationAccountName,
                    recommendationType,
                    recommendationId,
                    entityType,
                })
            );

            const results = await Promise.allSettled(duplicatePromises);

            const successfulResults = results.filter(result => result.status === 'fulfilled');
            const numSuccessfulResults = successfulResults.length;
            const failedResults = results.filter(result => result.status === 'rejected');

            const areNoSuccessfulResults = numSuccessfulResults === 0;
            if (areNoSuccessfulResults) {
                throw failedResults[0].reason;
            }

            const responses = successfulResults.map(result => result.value);
            const reportId = isProgrammaticAccount(account) ? CAMPAIGN_REPORT_ID : CREATIVE_REPORT_ID;
            const finalResponse = responses[responses.length - 1];
            const areNoFailedResults = failedResults.length === 0;

            batch(() => {
                dispatch(duplicateCampaignSuccess(finalResponse));

                if (areNoFailedResults) {
                    dispatch(addIndication(successIndication));
                } else {
                    dispatch(addIndication(generatePartialSuccessIndication(numSuccessfulResults, numberOfCopies)));
                }

                dispatch(selectCampaignAndReport(finalResponse, reportId, navigate));
            });

            const transformedResponses = responses.map(response => fromGWtransformers[entityType](response));
            return transformedResponses;
        } catch (error) {
            batch(() => {
                dispatch(duplicateCampaignError(error));
                dispatch(addIndication(generateErrorIndication(error)));
            });
            throw error;
        }
    };

export default duplicateCampaign;
